import React, { useState, useEffect, useRef } from 'react';
import {
  Star,
  ShieldCheck,
  MapPin,
  Sparkles,
  MessageSquarePlus,
  HeartHandshake,
  CheckCircle2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  getReviewsList,
  getReviewStats,
  syncReviewsFromRemote,
  CustomerReview,
  ReviewStats,
  REVIEWS_UPDATED_EVENT
} from '../services/reviewService';
import { WriteReviewModal } from './WriteReviewModal';

interface ReviewsSectionProps {
  onOpenWriteReview?: () => void;
}

export const ReviewsSection: React.FC<ReviewsSectionProps> = () => {
  const [reviews, setReviews] = useState<CustomerReview[]>(getReviewsList);
  const [stats, setStats] = useState<ReviewStats>(getReviewStats);
  const [activeFilter, setActiveFilter] = useState<'all' | '5star' | 'facial' | 'waxing'>('all');
  const [isWriteModalOpen, setIsWriteModalOpen] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<{ canScrollLeft: boolean; canScrollRight: boolean }>({
    canScrollLeft: false,
    canScrollRight: true
  });

  useEffect(() => {
    // Initial fetch & remote sync
    syncReviewsFromRemote().then((result) => {
      if (result.success) {
        setReviews(result.reviews);
        setStats(result.stats);
      }
    });

    const handleUpdate = () => {
      setReviews(getReviewsList());
      setStats(getReviewStats());
    };

    window.addEventListener(REVIEWS_UPDATED_EVENT, handleUpdate);
    return () => window.removeEventListener(REVIEWS_UPDATED_EVENT, handleUpdate);
  }, []);

  const updateScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setScrollState({
        canScrollLeft: scrollLeft > 10,
        canScrollRight: scrollLeft < scrollWidth - clientWidth - 10
      });
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollButtons, { passive: true });
      updateScrollButtons();
      return () => el.removeEventListener('scroll', updateScrollButtons);
    }
  }, [reviews, activeFilter]);

  const scrollCards = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = Math.max(260, scrollRef.current.clientWidth * 0.82);
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const filteredReviews = reviews.filter((r) => {
    if (activeFilter === '5star') return r.rating === 5;
    if (activeFilter === 'facial') {
      const text = `${r.serviceNames || ''} ${r.reviewText}`.toLowerCase();
      return text.includes('facial') || text.includes('cleanup') || text.includes('glow');
    }
    if (activeFilter === 'waxing') {
      const text = `${r.serviceNames || ''} ${r.reviewText}`.toLowerCase();
      return text.includes('wax') || text.includes('threading') || text.includes('eyebrow');
    }
    return true;
  });

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const formatReviewDate = (dateStr?: string) => {
    if (!dateStr) return 'Verified Booking';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'Verified Booking';
    }
  };

  return (
    <section className="blishka-reviews-section">
      <div className="reviews-container">
        {/* Section Header */}
        <div className="reviews-header">
          <div className="reviews-badge-pill">
            <Sparkles size={13} />
            <span>Verified Customer Ratings</span>
          </div>

          <h2 className="reviews-title">
            Loved by Women in Bhilai & Durg
          </h2>

          <p className="reviews-subtitle">
            Real doorstep parlour experiences from verified female clients across Bhilai, Durg & nearby sectors.
          </p>
        </div>

        {/* ── AGGREGATE RATING OVERVIEW CARD ── */}
        <div className="reviews-summary-card">
          {/* Mobile Top Row / Desktop Left Column */}
          <div className="reviews-mobile-top-row">
            <div className="reviews-score-col">
              <div className="reviews-big-number">
                {stats.averageRating.toFixed(1)}
              </div>
              <div className="reviews-score-details">
                <div className="reviews-stars-row">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={16} fill="#f59e0b" color="#f59e0b" />
                  ))}
                </div>
                <div className="reviews-verified-count">
                  {stats.totalReviews} Verified Ratings
                </div>
                <div className="reviews-positive-badge">
                  ✓ 99% Positive Doorstep Feedback
                </div>
              </div>
            </div>

            {/* Mobile Write Review Button (inline next to rating) */}
            <button
              type="button"
              onClick={() => setIsWriteModalOpen(true)}
              className="reviews-write-btn mobile-write-btn"
            >
              <MessageSquarePlus size={14} />
              <span>Write Review</span>
            </button>
          </div>

          {/* Rating Distribution Breakdown (Desktop only) */}
          <div className="reviews-breakdown-bars">
            {[5, 4, 3, 2, 1].map((stars) => {
              const count = stats.starCounts[stars] || 0;
              const pct = stats.totalReviews > 0 ? Math.round((count / stats.totalReviews) * 100) : 0;
              return (
                <div key={stars} className="reviews-bar-row">
                  <span className="reviews-bar-label">
                    {stars} ★
                  </span>
                  <div className="reviews-bar-bg">
                    <div
                      className={`reviews-bar-fill ${stars === 5 ? 'reviews-bar-fill-5' : 'reviews-bar-fill-other'}`}
                      style={{
                        width: `${stars === 5 ? (pct || 93) : stars === 4 ? (pct || 7) : 0}%`
                      }}
                    />
                  </div>
                  <span className="reviews-bar-pct">
                    {stars === 5 ? '93%' : stars === 4 ? '7%' : '0%'}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Trust Guarantees & Call to Action */}
          <div className="reviews-trust-col">
            <div className="reviews-trust-item">
              <ShieldCheck size={16} color="var(--blishka-berry)" />
              <span>100% Female Beauticians</span>
            </div>
            <div className="reviews-trust-item">
              <CheckCircle2 size={16} color="#2e7d32" />
              <span>Sealed Disposable Kits</span>
            </div>
            <div className="reviews-trust-item">
              <HeartHandshake size={16} color="var(--blishka-berry)" />
              <span>Pay After Service</span>
            </div>

            {/* Desktop Write Review Button */}
            <button
              type="button"
              onClick={() => setIsWriteModalOpen(true)}
              className="reviews-write-btn desktop-write-btn"
            >
              <MessageSquarePlus size={16} />
              <span>Write a Review</span>
            </button>
          </div>
        </div>

        {/* ── FILTER BUTTONS (Smooth Horizontal Scroll on Mobile) ── */}
        <div className="reviews-filter-track">
          {[
            { id: 'all', label: 'All Reviews' },
            { id: '5star', label: '5-Star Loved' },
            { id: 'facial', label: 'Facials & Cleanup' },
            { id: 'waxing', label: 'Waxing & Threading' }
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveFilter(tab.id as any)}
              className={`reviews-filter-pill ${activeFilter === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── REVIEWS CARDS TRACK (HORIZONTAL SCROLL ON MOBILE, GRID ON DESKTOP) ── */}
        <div className="reviews-track-wrapper">
          <div ref={scrollRef} className="reviews-cards-track">
            {filteredReviews.map((review) => (
              <div key={review.id} className="review-card">
                {/* Card Top */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    {/* Avatar & Name */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                      <div className="review-card-avatar">
                        {getInitials(review.customerName)}
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                          <h4 className="review-card-name">
                            {review.customerName}
                          </h4>
                          {review.isVerified && (
                            <span title="Verified Doorstep Client" style={{ display: 'inline-flex', color: '#2e7d32' }}>
                              <CheckCircle2 size={13} />
                            </span>
                          )}
                        </div>
                        {review.area && (
                          <div className="review-card-area">
                            <MapPin size={11} />
                            <span>{review.area}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Date */}
                    <div className="review-card-date">
                      {formatReviewDate(review.createdAt)}
                    </div>
                  </div>

                  {/* Stars Rating */}
                  <div className="review-card-stars">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        size={15}
                        fill={s <= review.rating ? '#f59e0b' : 'none'}
                        color={s <= review.rating ? '#f59e0b' : '#d1d5db'}
                      />
                    ))}
                  </div>

                  {/* Service Tag */}
                  {review.serviceNames && (
                    <div className="review-service-pill">
                      ✨ {review.serviceNames}
                    </div>
                  )}

                  {/* Review Text */}
                  <p className="review-text-quote">
                    "{review.reviewText}"
                  </p>
                </div>

                {/* Card Footer */}
                <div className="review-card-footer">
                  <span className="review-verified-badge">
                    <ShieldCheck size={13} />
                    Verified Doorstep Visit
                  </span>
                  {review.bookingRef && (
                    <span className="review-booking-ref">
                      {review.bookingRef}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Swipe Cue & Navigation Arrows */}
          <div className="reviews-mobile-swipe-cue">
            <span>👈 Swipe to read reviews ({filteredReviews.length}) 👉</span>
            <div className="reviews-nav-arrows">
              <button
                type="button"
                onClick={() => scrollCards('left')}
                className="reviews-nav-arrow-btn"
                aria-label="Scroll left"
                style={{ opacity: scrollState.canScrollLeft ? 1 : 0.4 }}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => scrollCards('right')}
                className="reviews-nav-arrow-btn"
                aria-label="Scroll right"
                style={{ opacity: scrollState.canScrollRight ? 1 : 0.4 }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Callout Banner */}
        <div className="reviews-bottom-banner">
          <h4 style={{ fontWeight: 800, color: 'var(--color-text-title)', margin: '0 0 0.35rem' }}>
            Booked a Doorstep Service with Blishka Recently?
          </h4>
          <p style={{ color: 'var(--color-text-muted)', margin: '0 0 0.85rem' }}>
            Help fellow women in Bhilai & Durg make confident salon choices.
          </p>
          <button
            type="button"
            onClick={() => setIsWriteModalOpen(true)}
            className="btn btn-outline"
            style={{
              borderColor: 'var(--blishka-berry)',
              color: 'var(--blishka-berry)',
              fontWeight: 700,
              borderRadius: '20px'
            }}
          >
            ★ Leave Your Rating & Review
          </button>
        </div>
      </div>

      {/* Write Review Modal */}
      <WriteReviewModal
        isOpen={isWriteModalOpen}
        onClose={() => setIsWriteModalOpen(false)}
        onReviewSubmitted={(newRev) => {
          setReviews([newRev, ...reviews]);
        }}
      />
    </section>
  );
};
